import type {
  EntityMappingDescriptor,
  EntityMappingDraft,
  EntityMappingOption,
  EntityMappingPayload,
  MappingFieldSpec,
  UnmappedEntity,
} from '../models/entityMapping.types'

const selected = (values: readonly string[] | undefined): boolean => Boolean(values?.length)

function entityId(descriptor: EntityMappingDescriptor, entity: UnmappedEntity): string | null {
  const value = entity[descriptor.idKey]
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null
}

export function defaultEntityMappingChannel(channels: readonly string[]): string | null {
  return channels.includes('meta_ads') ? 'meta_ads' : (channels[0] ?? null)
}

export function entityMappingChannels(entities: readonly UnmappedEntity[]): string[] {
  return [
    ...new Set(
      entities.flatMap((entity) =>
        typeof entity.marketing_channel === 'string' ? [entity.marketing_channel] : [],
      ),
    ),
  ]
}

export function buildEntityMappingDraft(
  descriptor: EntityMappingDescriptor,
  entities: readonly UnmappedEntity[],
): EntityMappingDraft {
  return Object.fromEntries(
    entities.flatMap((entity) => {
      const id = entityId(descriptor, entity)
      return id
        ? [[id, Object.fromEntries(descriptor.fields.map((field) => [field.draftKey, []]))]]
        : []
    }),
  )
}

export function validateEntityMappingDraft(
  descriptor: EntityMappingDescriptor,
  draft: EntityMappingDraft,
): string[] {
  const required = descriptor.fields.filter((field) => field.required)
  if (required.length <= 1) return []
  return Object.entries(draft).flatMap(([id, row]) => {
    const selectedCount = required.filter((field) => selected(row[field.draftKey])).length
    return selectedCount > 0 && selectedCount < required.length ? [id] : []
  })
}

function payloadValues(field: MappingFieldSpec, row: Record<string, string[]>): string[] {
  const values = row[field.draftKey] ?? []
  return field.filterEmpty ? values.filter(Boolean) : [...values]
}

export function buildEntityMappingPayload(
  descriptor: EntityMappingDescriptor,
  draft: EntityMappingDraft,
): EntityMappingPayload {
  const mappings = Object.entries(draft).flatMap(([id, row]) => {
    if (!descriptor.fields.some((field) => selected(row[field.draftKey]))) return []
    const mapping: Record<string, string | string[]> = { [descriptor.idKey]: id }
    for (const field of descriptor.fields) mapping[field.payloadKey] = payloadValues(field, row)
    return [mapping]
  })
  return { mappings }
}

function optionValue(option: EntityMappingOption, key: string | null): string | null {
  if (key === null) return typeof option === 'string' ? option : null
  if (!option || typeof option !== 'object') return null
  const value = option[key]
  return typeof value === 'string' ? value : null
}

export function availableEntityMappingOptions(
  descriptor: EntityMappingDescriptor,
  draftKey: string,
  row: Record<string, string[]>,
  options: readonly EntityMappingOption[],
): string[] {
  const field = descriptor.fields.find((candidate) => candidate.draftKey === draftKey)
  if (!field) return []
  return [
    ...new Set(
      options.flatMap((option) => {
        const matches = descriptor.fields.every((candidate) => {
          if (candidate.draftKey === draftKey) return true
          const values = row[candidate.draftKey] ?? []
          if (!values.length) return true
          const value = optionValue(option, candidate.optionValueKey)
          return value !== null && values.includes(value)
        })
        const value = matches ? optionValue(option, field.optionValueKey) : null
        return value ? [value] : []
      }),
    ),
  ]
}
